import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";

const GUIDES = [
  {
    k: "01",
    t: "What is vibe coding?",
    b: "The honest 2026 explainer — origins, tools, what it's great at, where it breaks, and how to ship vibe-coded apps you'd trust in production.",
    to: "/guides/what-is-vibe-coding",
    tag: "Cornerstone guide",
  },
  {
    k: "02",
    t: "Lovable vs Cursor vs Claude Code vs Codex",
    b: "Side-by-side comparison of the four AI coding tools that actually matter in 2026 — features, pricing, models, and which one to pick.",
    to: "/guides/lovable-vs-cursor",
    tag: "Tool comparison",
  },
  {
    k: "03",
    t: "The iOS 26 Safari white bar, explained",
    b: "Why Safari paints a white bar over the notch, why your sticky header is the culprit, and the tested recipe for a true edge-to-edge website on iPhone.",
    to: "/guides/ios-safari-white-bar-fix",
    tag: "Battle report",
  },
  {
    k: "04",
    t: "How to build an MCP server with Lovable",
    b: "What MCP actually is, when it's worth building, the three files you write in Lovable, and the auth decision that decides whether the whole thing is safe.",
    to: "/guides/how-to-build-an-mcp-server-with-lovable",
    tag: "Build guide",
  },
  {
    k: "05",
    t: "How to export your data from Lovable Cloud",
    b: "The exact click path for a database export, the 5 GB and 24-hour limits, what the dump leaves behind, and how to restore it into your own Supabase.",
    to: "/guides/export-data-from-lovable-cloud",
    tag: "Migration guide",
  },
  {
    k: "06",
    t: "Vibe coding help",
    b: "What to do when the AI keeps breaking the same thing, the published build doesn't match the preview, or the app is 80% done and stuck there.",
    to: "/guides/vibe-coding-help",
    tag: "Troubleshooting",
  },
  {
    k: "07",
    t: "Best AI app builders in 2026",
    b: "Lovable, Bolt, Replit, v0 and Base44 compared on the boring criterion that matters — what happens after the demo works.",
    to: "/guides/best-ai-app-builders",
    tag: "Tool comparison",
  },
];


export const Route = createFileRoute("/guides/")({
  head: () => ({
    meta: [
      { title: "Guides · Vibe coding & AI coding tools | Vibing Company" },
      {
        name: "description",
        content:
          "Independent guides on vibe coding, Lovable, Cursor, Claude Code and Codex — written by a senior team that builds with them every week.",
      },
      { property: "og:title", content: "Guides · Vibe coding & AI coding tools" },
      ...ogImageMeta("guides", "Share card: guides on vibe coding and AI build tools"),
      {
        property: "og:description",
        content:
          "Honest, up-to-date guides on vibe coding and the AI coding tools we use every day.",
      },
      { property: "og:url", content: "https://vibing.inc/guides" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Vibing Company guides",
          itemListElement: GUIDES.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://vibing.inc${g.to}`,
            name: g.t,
          })),
        }),
      },
    ],
  }),
  component: GuidesHub,
});

function GuidesHub() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Grid />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="g-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1
        id="g-h"
        className="font-display tracking-[-0.035em] leading-[1.02] max-w-[18ch]"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
      >
        Field notes from the build.
      </h1>
      <p className="mt-10 max-w-[62ch] text-xl md:text-2xl leading-snug text-foreground">
        Independent write-ups on the AI coding tools we use every day. We're not affiliated with Lovable, Cursor, Anthropic or OpenAI — just opinionated practitioners.
      </p>
    </section>
  );
}

function Grid() {
  return (
    <section aria-labelledby="g-list" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24">
      <h2 id="g-list" className="sr-only">All guides</h2>
      <ul className="grid md:grid-cols-2 gap-5">
        {GUIDES.map((g) => (
          <li key={g.to} className="rounded-3xl border border-border bg-background p-8 md:p-10 flex flex-col">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">{g.tag}</span>
            <h3
              className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
              style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
            >
              {g.t}
            </h3>
            <p className="mt-4 text-base md:text-lg text-foreground/80 leading-relaxed">{g.b}</p>
            <div className="mt-8 pt-6 border-t border-border/60">
              <Link to={g.to} className="inline-flex items-center gap-2 font-display text-electric underline underline-offset-4">
                Read the guide <span aria-hidden>→</span>
              </Link>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 text-center">
      <h2 className="fluid-h2 font-display max-w-[22ch] mx-auto">Want us to ship the build, not just the guide?</h2>
      <div className="mt-10 flex justify-center">
        <TalkToJonButton variant="dark" immersive />
      </div>
    </section>
  );
}
